/* CoachOverlay.tsx — anchored coachmark for 'coach' steps.

   Spotlights a real control (dim everything else via a spreading box-shadow "hole",
   so the control underneath stays clickable — the user really does the thing) and
   floats a Sofi bubble next to it. Reads the host page's CSS vars (--card/--text/
   --accent …) so it sits natively on whatever page it's driving. */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { POSE, type Pose } from '../Onboarding/onboardingKit';
import { measureAnchor, placeBubble, scrollAnchorIntoView, type AnchorRect, type BubblePos } from './spotlight';
import type { Placement } from './types';

interface Props {
  anchor: string;
  placement: Placement;
  pose: Pose;
  title: string;
  body: ReactNode;
  hint?: string;
  stepIndex: number;
  stepCount: number;
  primaryLabel: string;
  primaryDisabled?: boolean;
  onPrimary: () => void;
  /** Optional "Do it for me" secondary action. */
  onAssist?: () => void;
  assistLabel?: string;
  onBack: (() => void) | null;
  onSkip: (() => void) | null;
  onExit: () => void;
}

const PAD = 8; // spotlight padding around the control

export function CoachOverlay(props: Props) {
  const { anchor, placement, pose, title, body, hint, stepIndex, stepCount, primaryLabel, primaryDisabled, onPrimary, onAssist, assistLabel, onBack, onSkip, onExit } = props;
  const { t } = useTranslation('common');
  const [rect, setRect] = useState<AnchorRect | null>(null);
  const [pos, setPos] = useState<BubblePos | null>(null);
  // When a foreground dialog (e.g. the media picker) opens, step aside entirely so
  // Sofi's dim + bubble don't fight the modal; resume when it closes.
  const [suppressed, setSuppressed] = useState(false);
  const bubbleRef = useRef<HTMLDivElement>(null);

  // Keep the spotlight glued to the control across scroll / resize / layout shifts
  // (editors seeding rows, sections expanding). Poll cheaply — the anchor may not
  // exist for a frame after onEnter switches sections.
  const remeasure = useCallback(() => {
    setRect(measureAnchor(anchor));
  }, [anchor]);

  useEffect(() => {
    scrollAnchorIntoView(anchor);
    remeasure();
    const tick = () => {
      remeasure();
      // A HeroUI/ARIA modal open on top of us → get out of the way.
      setSuppressed(!!document.querySelector('[role="dialog"],[aria-modal="true"]'));
    };
    tick();
    const id = window.setInterval(tick, 200);
    window.addEventListener('scroll', remeasure, true);
    window.addEventListener('resize', remeasure);
    return () => {
      window.clearInterval(id);
      window.removeEventListener('scroll', remeasure, true);
      window.removeEventListener('resize', remeasure);
    };
  }, [anchor, remeasure]);

  // Reposition the bubble once we know both the anchor rect and the bubble size.
  useLayoutEffect(() => {
    if (!rect || !bubbleRef.current) return;
    const b = bubbleRef.current;
    setPos(placeBubble(
      { top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 },
      placement, b.offsetWidth, b.offsetHeight,
    ));
  }, [rect, placement, title, body, hint]);

  // Hidden while a foreground modal is open — the runner keeps polling underneath,
  // so auto-advance (e.g. media attached → editor valid) still fires seamlessly.
  if (suppressed) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 4000, pointerEvents: 'none' }}>
      {/* Spotlight hole — box-shadow dims the rest; pointer-events none so the
          control underneath stays interactive. Fallback dim when the anchor is
          momentarily missing keeps the screen from flashing undimmed. */}
      {rect ? (
        <div
          style={{
            position: 'fixed',
            top: rect.top - PAD, left: rect.left - PAD,
            width: rect.width + PAD * 2, height: rect.height + PAD * 2,
            borderRadius: 12, pointerEvents: 'none',
            boxShadow: '0 0 0 9999px color-mix(in srgb, var(--om-page) 62%, transparent), 0 0 0 2px var(--accent, var(--om-accent)), 0 0 22px 4px var(--accent-glow, color-mix(in srgb, var(--om-accent) 50%, transparent))',
            transition: 'top .25s cubic-bezier(.4,0,.2,1), left .25s cubic-bezier(.4,0,.2,1), width .25s, height .25s',
          }}
        />
      ) : (
        <div style={{ position: 'fixed', inset: 0, background: 'color-mix(in srgb, var(--om-page) 62%, transparent)', pointerEvents: 'none' }} />
      )}

      {/* Bubble */}
      <div
        ref={bubbleRef}
        style={{
          position: 'fixed',
          top: pos?.top ?? -9999, left: pos?.left ?? -9999,
          width: 336, maxWidth: 'calc(100vw - 24px)', pointerEvents: 'auto',
          background: 'var(--card, var(--om-legacy-card))', color: 'var(--text, var(--om-legacy-text))',
          border: '1px solid var(--line-2, color-mix(in srgb, var(--om-ink) 14%, transparent))',
          borderRadius: 18, padding: 18, boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
          fontFamily: 'var(--font-sans)',
          opacity: pos ? 1 : 0, transition: 'top .25s cubic-bezier(.4,0,.2,1), left .25s cubic-bezier(.4,0,.2,1), opacity .2s',
        }}
      >
        {/* header: mini-Sofi + progress + exit */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 12 }}>
          <div
            style={{
              width: 46, height: 46, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
              background: 'var(--accent-soft, color-mix(in srgb, var(--om-accent) 16%, transparent))', border: '1px solid var(--theme-line, color-mix(in srgb, var(--om-accent) 32%, transparent))',
              display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            }}
          >
            <img key={pose} src={POSE[pose]} alt="Sofi" style={{ width: 40, height: 'auto', animation: 'omc-pop .28s ease' }} />
          </div>
          <div style={{ flex: 1, fontSize: 11.5, fontWeight: 600, letterSpacing: '.04em', color: 'var(--faint, var(--om-dim))' }}>
            SOFI · {stepIndex + 1}/{stepCount}
          </div>
          <button
            onClick={onExit}
            aria-label="Close setup"
            style={{ width: 26, height: 26, borderRadius: 8, background: 'transparent', border: '1px solid var(--line-2, color-mix(in srgb, var(--om-ink) 14%, transparent))', color: 'var(--muted, var(--om-sub-2))', cursor: 'pointer', fontSize: 15, lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 800, letterSpacing: -0.2, marginBottom: 6 }}>{title}</div>
        <div style={{ fontSize: 14, lineHeight: 1.55, color: 'var(--muted, var(--om-sub-2))' }}>{body}</div>

        {hint && (
          <div
            style={{
              marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12.5, fontWeight: 600,
              color: 'var(--accent-ico, var(--om-accent-2))', background: 'var(--accent-soft, color-mix(in srgb, var(--om-accent) 16%, transparent))',
              border: '1px solid var(--theme-line, color-mix(in srgb, var(--om-accent) 32%, transparent))', borderRadius: 999, padding: '5px 11px',
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent, var(--om-accent))', animation: 'omc-pulse 1.4s ease-in-out infinite' }} />
            {hint}
          </div>
        )}

        {/* actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
          {onBack && (
            <button onClick={onBack} style={ghostBtn}>{t('tutorial.back')}</button>
          )}
          <div style={{ flex: 1 }} />
          {onSkip && (
            <button onClick={onSkip} style={{ ...ghostBtn, color: 'var(--faint, var(--om-dim))' }}>{t('tutorial.skip')}</button>
          )}
          {onAssist && (
            <button onClick={onAssist} style={softBtn}>{assistLabel ?? t('tutorial.doItForMe')}</button>
          )}
          <button
            onClick={primaryDisabled ? undefined : onPrimary}
            disabled={primaryDisabled}
            style={primaryDisabled ? { ...primaryBtn, opacity: 0.45, cursor: 'not-allowed', boxShadow: 'none' } : primaryBtn}
          >
            {primaryLabel}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes omc-pop { from { opacity: 0; transform: scale(.9) } to { opacity: 1; transform: none } }
        @keyframes omc-pulse { 0%,100% { opacity: .4; transform: scale(1) } 50% { opacity: 1; transform: scale(1.5) } }
      `}</style>
    </div>
  );
}

const baseBtn: CSSProperties = {
  fontFamily: 'inherit', fontWeight: 700, fontSize: 13.5, borderRadius: 10, padding: '9px 15px', cursor: 'pointer', border: '1px solid transparent',
};
const ghostBtn: CSSProperties = { ...baseBtn, background: 'transparent', color: 'var(--muted, var(--om-sub-2))', border: '1px solid var(--line-2, color-mix(in srgb, var(--om-ink) 14%, transparent))' };
const softBtn: CSSProperties = { ...baseBtn, background: 'var(--accent-soft, color-mix(in srgb, var(--om-accent) 16%, transparent))', color: 'var(--accent-ico, var(--om-accent-2))', border: '1px solid var(--theme-line, color-mix(in srgb, var(--om-accent) 32%, transparent))' };
const primaryBtn: CSSProperties = { ...baseBtn, background: 'var(--accent, var(--om-accent))', color: 'var(--accent-on, var(--om-on-accent))', boxShadow: '0 10px 24px var(--accent-glow, color-mix(in srgb, var(--om-accent) 50%, transparent))' };
